<template>
  <div ref="root" :aria-label="label" :class="{ 'seg--block': block, 'seg--tall': tall, 'seg--ready': thumbReady, 'seg--animate': animate }" class="seg"
       role="radiogroup">
    <span v-if="thumbReady" :style="thumbStyle" aria-hidden="true" class="seg__thumb"/>
    <button v-for="item in items" :key="String(item.value)" v-tip="item.disabled ? item.reason : ''" :aria-checked="String(item.value === value)"
            :aria-disabled="item.disabled ? 'true' : undefined" :class="{ 'seg__item--active': item.value === value, 'seg__item--disabled': item.disabled }"
            class="seg__item" role="radio" type="button" @click="!item.disabled && $emit('input', item.value)">
      <v-icon v-if="item.icon" :small="!tall" class="seg__icon">{{ item.icon }}</v-icon>
      <span class="seg__text">{{ item.text }}</span>
      <span v-if="item.count !== undefined" class="seg__count">{{ item.count }}</span>
    </button>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

/** disabled items stay visible with their reason as tooltip (never hidden without a reason) */
export type SegmentItem = { value: unknown; text: string; icon?: string; count?: number; disabled?: boolean; reason?: string };

type ThumbRect = { left: number; top: number; width: number; height: number };

/**
 * Modern segmented control (single choice), replaces outlined chip groups.
 * The white background of the active item is a separate thumb that slides to the new selection.
 */
export default Vue.extend({
  props: {
    value: {required: false, default: null},
    items: {type: Array as () => SegmentItem[], required: true},
    label: {type: String, default: ''},
    /** Stretch over the full width */
    block: {type: Boolean, default: false},
    /** Icon above the text */
    tall: {type: Boolean, default: false},
  },
  data: () => ({
    thumb: undefined as ThumbRect | undefined,
    /** Enabled shortly after the first measurement, so the thumb does not slide in from the left edge */
    animate: false,
    observer: undefined as ResizeObserver | undefined,
    frame: 0,
  }),
  computed: {
    activeIndex(): number {
      return this.items.findIndex((item) => item.value === this.value);
    },
    thumbReady(): boolean {
      return !!this.thumb && this.activeIndex !== -1;
    },
    thumbStyle(): Record<string, string> {
      if (!this.thumb) {
        return {};
      }
      return {
        width: `${this.thumb.width}px`,
        height: `${this.thumb.height}px`,
        transform: `translate(${this.thumb.left}px, ${this.thumb.top}px)`,
      };
    },
  },
  methods: {
    /**
     * Buttons in DOM order (= order of items). Not $refs of the v-for: Vue 2 appends the ref of an item added later
     * (e.g. "Bild" between "Text" and "Keine") at the end, so the index pointed at the wrong button.
     */
    buttons(): HTMLElement[] {
      const root = this.$refs.root as HTMLElement | undefined;
      return root ? Array.from(root.children).filter((el): el is HTMLElement => el.classList.contains('seg__item')) : [];
    },
    measure() {
      const active = this.buttons()[this.activeIndex];
      // Hidden (e.g. in a closed dialog): no thumb yet, and no slide-in once it becomes visible
      if (!active || !active.offsetWidth) {
        this.thumb = undefined;
        this.animate = false;
        return;
      }
      this.thumb = {left: active.offsetLeft, top: active.offsetTop, width: active.offsetWidth, height: active.offsetHeight};
      if (!this.animate) {
        window.clearTimeout(this.frame);
        this.frame = window.setTimeout(() => {
          this.animate = true;
        }, 50);
      }
    },
    observe() {
      if (typeof ResizeObserver === 'undefined') {
        return;
      }
      this.observer?.disconnect();
      this.observer = new ResizeObserver(() => this.measure());
      const root = this.$refs.root as HTMLElement | undefined;
      if (root) {
        this.observer.observe(root);
      }
      for (const button of this.buttons()) {
        this.observer.observe(button);
      }
    },
  },
  watch: {
    value() {
      this.$nextTick(this.measure);
    },
    items() {
      this.$nextTick(() => {
        this.observe();
        this.measure();
      });
    },
  },
  mounted() {
    this.measure();
    this.observe();
  },
  beforeDestroy() {
    this.observer?.disconnect();
    window.clearTimeout(this.frame);
  },
});
</script>

<style>
.seg {
  position: relative;
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 12px;
  background: rgba(15, 23, 42, .06);
}

.seg--block {
  display: flex;
  width: 100%;
}

.seg__thumb {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 9px;
  background: #FFFFFF;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08), 0 2px 8px rgba(15, 23, 42, .08);
  pointer-events: none;
}

.seg--animate .seg__thumb {
  transition: transform .28s cubic-bezier(.4, 0, .2, 1), width .28s cubic-bezier(.4, 0, .2, 1), height .28s cubic-bezier(.4, 0, .2, 1);
}

.seg__item {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: rgba(15, 23, 42, .68);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background .15s ease, color .2s ease, box-shadow .15s ease;
}

.seg--block .seg__item {
  flex: 1 1 0;
  min-width: 0;
}

.seg--tall .seg__item {
  flex-direction: column;
  height: 54px;
  gap: 2px;
  font-size: 12px;
}

.seg__item:hover {
  color: rgba(15, 23, 42, .9);
}

.seg__item:focus-visible {
  outline: 2px solid #1976D2;
  outline-offset: 1px;
}

.seg__item--active {
  background: #FFFFFF;
  color: #0D47A1;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08), 0 2px 8px rgba(15, 23, 42, .08);
}

/* Once the thumb is measured it carries the background, the active item only changes its text color */
.seg--ready .seg__item--active {
  background: transparent;
  box-shadow: none;
}

.seg__item .seg__icon {
  color: inherit !important;
  transition: color .2s ease;
}

.seg__count {
  font-size: 11px;
  font-weight: 600;
  padding: 0 6px;
  border-radius: 8px;
  background: rgba(15, 23, 42, .07);
  color: inherit;
  transition: background .2s ease;
}

.seg__item--active .seg__count {
  background: #E3F2FD;
}

.theme--dark .seg {
  background: rgba(255, 255, 255, .08);
}

.theme--dark .seg__item {
  color: rgba(255, 255, 255, .7);
}

.theme--dark .seg__item--active, .theme--dark .seg__thumb {
  background: #2C2C2C;
}

.theme--dark .seg__item--active {
  color: #90CAF9;
}

.theme--dark .seg__item:hover {
  color: rgba(255, 255, 255, .92);
}

.theme--dark .seg__count {
  background: rgba(255, 255, 255, .1);
}

.theme--dark .seg__item--active .seg__count {
  background: rgba(144, 202, 249, .18);
}

.theme--dark .seg__item:focus-visible {
  outline-color: #90CAF9;
}

.theme--dark .seg--ready .seg__item--active {
  background: transparent;
}

@media (prefers-reduced-motion: reduce) {
  .seg--animate .seg__thumb, .seg__item, .seg__item .seg__icon, .seg__count {
    transition: none;
  }
}
.seg__item--disabled {
  opacity: .45;
  cursor: not-allowed;
}
</style>
